import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from './firebase';
import {
  onAuthStateChanged,
  signInWithPopup,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
} from 'firebase/auth';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';

const AuthContext = createContext(null);

async function ensureProfile(firebaseUser) {
  const userRef = doc(db, 'users', firebaseUser.uid);
  const snap    = await getDoc(userRef);
  if (snap.exists()) return;
  const newProfile = {
    email:       firebaseUser.email,
    displayName: firebaseUser.displayName || firebaseUser.email,
    roles:       [],
    approved:    false,
    createdAt:   new Date().toISOString(),
  };
  await setDoc(userRef, newProfile);
}

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let profileUnsub = null;
    const authUnsub = onAuthStateChanged(auth, async (firebaseUser) => {
      // Clean up previous profile listener
      if (profileUnsub) { profileUnsub(); profileUnsub = null; }

      if (firebaseUser) {
        try {
          await ensureProfile(firebaseUser);
          setUser(firebaseUser);
          // Live-listen to profile doc — picks up role changes instantly
          profileUnsub = onSnapshot(doc(db, 'users', firebaseUser.uid), (snap) => {
            if (snap.exists()) setProfile(snap.data());
          });
        } catch {
          setUser(firebaseUser);
          setProfile(null);
        }
      } else {
        setUser(null);
        setProfile(null);
      }
      setLoading(false);
    });
    return () => {
      authUnsub();
      if (profileUnsub) profileUnsub();
    };
  }, []);

  const signIn = async () => {
    // ── MICROSOFT SWAP: see firebase.js for instructions ──────────────────
    // Replace GoogleAuthProvider with OAuthProvider('microsoft.com') + tenant ID.
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    await signInWithPopup(auth, provider);
    // ─────────────────────────────────────────────────────────────────────
  };

  const signOut = () => {
    try { localStorage.removeItem('s2s_activeRole'); } catch {}
    return firebaseSignOut(auth);
  };

  return (
    <AuthContext.Provider value={{ user, profile, loading, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
